import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Super Admin only. Merges this login with an employee profile (e.g. the CEO's INT0001) so one
 * login serves the admin portal and the employee view. The old staff login is disabled, not deleted,
 * and "Unlink" restores it.
 */
export default function LinkMyProfile() {
  const { hasEmployeeProfile, employeeProfile, refetch, setViewMode } = useAuth();
  const [code, setCode] = useState("INT0001");
  const [disableOld, setDisableOld] = useState(true);
  const [exempt, setExempt] = useState(true);
  const [busy, setBusy] = useState(false);

  const findEmployee = async () => {
    const { data } = await api.get("/employees", { params: { search: code.trim() } });
    return (data || []).find((e) => (e.employee_id || "").trim().toLowerCase() === code.trim().toLowerCase());
  };

  const link = async () => {
    setBusy(true);
    try {
      const emp = await findEmployee();
      if (!emp) { toast.error(`No employee found with ID ${code}`); return; }
      await api.post(`/employees/${emp.id}/link-to-me`, { disable_old_login: disableOld, exempt });
      toast.success(`Linked to ${emp.employee_id}. Use "My Space" at the top to switch views.`);
      await refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!window.confirm(`Unlink your login from ${employeeProfile.employee_id}? The old staff login will be restored.`)) return;
    setBusy(true);
    try {
      await api.post(`/employees/${employeeProfile.id}/unlink-from-me`);
      toast.success("Unlinked. The employee's own login is active again.");
      setViewMode("admin");
      await refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200 shadow-none p-5 mb-5" data-testid="link-my-profile">
      <div className="flex items-start gap-3">
        <UserRound className="h-5 w-5 text-teal mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-navy">My employee profile</p>
          {hasEmployeeProfile ? (
            <>
              <p className="text-sm text-slate-600 mt-1">
                Your Super Admin login is linked to <b>{employeeProfile.name} ({employeeProfile.employee_id})</b>
                {employeeProfile.exempt ? ", exempt from document, policy, leave and attendance rules" : ""}.
                Use <b>My Space</b> in the top bar to switch to the employee view (team approvals, notifications).
              </p>
              <Button size="sm" variant="outline" className="mt-3" onClick={unlink} disabled={busy}>Unlink</Button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-1">
                Merge this login with your employee profile so you don't need a separate Employee ID login for team leave approvals and notifications.
              </p>
              <div className="grid sm:grid-cols-[200px_1fr] gap-3 items-end mt-3">
                <div><Label>Employee ID</Label><Input value={code} onChange={(e) => setCode(e.target.value)} /></div>
                <div className="text-sm text-slate-600 space-y-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={disableOld} onChange={(e) => setDisableOld(e.target.checked)} /> Disable the old Employee ID login (restorable)</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={exempt} onChange={(e) => setExempt(e.target.checked)} /> Exempt from document, policy, leave and attendance rules</label>
                </div>
              </div>
              <Button size="sm" className="mt-3 bg-navy hover:bg-navy-700" onClick={link} disabled={busy || !code.trim()}>Link my login</Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
